import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  Link,
  Alert,
  AlertTitle,
  Grid,
} from "@mui/material";
import PasswordTextField from "components/general/PasswordTextField";
import StandardTextField from "components/general/StandardTextField";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import useTranslate from "hooks/useTranslate";
import { useAppSelector } from "app/hooks";
import { Link as RouterLink } from "react-router";

import {
  ILoginError,
  LoginErrorStatus,
} from "adminPanel/user/models/LoginErrorStatus";
import LanguageSelector from "components/general/LanguageSelector";
import useIsSmUp from "hooks/useIsSmUp";
import i18n from "localization/i18n";
import SignLogo from "components/icons/SignLogo";
import Button from "components/Button";
import { useThemeColors } from "theme/hooks/useThemeColors";
import getOrganization from "helpers/getOrganization";
import { useNavigate } from "react-router";
import { useAppDispatch } from "app/hooks";
import { setOrganizationId } from "adminPanel/user/userSlice";
import AuthLeftPanel from "./AuthLeftSide";

export interface ILoginFormData {
  username: string;
  organizationID: string;
  password: string;
  rememberMe: boolean;
}

export interface ILoginPageProps {
  submitHandler: (data: any) => void;
  resendEmail: () => void;
  error?: ILoginError;
  /** DOM contract: lands on the page container. Defaults to `login-page`. */
  "data-testid"?: string;
}

export default function LoginPage({
  submitHandler,
  resendEmail,
  error,
  "data-testid": testId = "login-page",
}: ILoginPageProps) {
  const { translate, messageTrans } = useTranslate("shared.signIn");
  const colors = useThemeColors();
  const isSmUp = useIsSmUp();
  const style = {
    container: {
      height: "100vh",
    },
    cardHeader: {
      alignItems: "center",
      justifyContent: "space-between",
    },
    containerLogin: {
      display: "flex",
      justifyContent: "center",
      right: 0,
      background: colors.background.new,
    },
    containerMobileLog: {
      backgroundImage: "url('/images/BGMobile.svg')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      justifyContent: "center",
      alignItems: "center",
      paddingX: 2,
      height: "85dvh",
    },
    cardContent: {
      width: isSmUp ? "35vw" : "100dvw",
    },

    title: {
      fontSize: 28,
      fontWeight: 600,
      mb: 1,
    },
    Link: {
      textDecoration: "none",
      color: colors.brand.primary,
      fontWeight: 500,
    },
    langSelect: isSmUp
      ? {
          color: colors.brand.primary,
        }
      : {
          position: "absolute",
          top: 22,
          right: i18n.language == "en" ? 5 : "unset",
          left: i18n.language == "ar" ? 5 : "unset",
          color: "white",
        },
    warning: {
      fontSize: 14,
      color: colors.status.error,
    },
  };

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const organizationIDFromStore = useAppSelector(
    (state) => state.user.organizationId,
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ILoginFormData>({
    defaultValues: {
      organizationID: organizationIDFromStore
        ? organizationIDFromStore
        : getOrganization(),
      username: "",
      password: "",
      rememberMe: false,
    },
  });
  const onSave = (data: ILoginFormData) => {
    const currentOrg = getOrganization();
    if (data.organizationID && data.organizationID !== currentOrg) {
      dispatch(setOrganizationId(data.organizationID));
      navigate(`/${data.organizationID}/login`, { replace: true });
    }
    const { username, ...rest } = data;
    submitHandler({ ...rest, name: username });
  };

  const alert = error && (
    <Alert severity="error" sx={style.warning} data-testid="login-error">
      <AlertTitle sx={{ color: colors.status.error }}>
        {" "}
        {translate(error.error.name)}
      </AlertTitle>
      <span>{error.error.value} </span>
    </Alert>
  );

  const cardActions = (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Button
        fullWidth
        onClick={handleSubmit(onSave)}
        type="submit"
        disableElevation
        data-testid="login-submit"
      >
        <Typography variant="bodyMediumRegular">
          {translate("signIn")}
        </Typography>
      </Button>
      {error && error.status === LoginErrorStatus.EmailIsNotVerified && (
        <Button
          fullWidth
          onClick={resendEmail}
          data-testid="login-resend-email"
        >
          {translate("resend")}
        </Button>
      )}
      <Typography color="textSecondary">
        {translate("createNewAccount")}{" "}
        <Link
          component={RouterLink}
          to={"/signup"}
          sx={style.Link}
          data-testid="login-signup-link"
        >
          {translate("signUp")}
        </Link>
      </Typography>
      <LanguageSelector />
    </Stack>
  );
  // 327701
  const cardHeader = (
    <Stack sx={style.cardHeader}>
      <Box>
        <SignLogo width={"300px"} height={"250px"} />
      </Box>
      <Typography sx={style.title}>{translate("login")}</Typography>
      <Stack
        direction={"row"}
        sx={{
          alignItems: "center",
        }}
      >
        <Typography color="textSecondary" variant="bodyMedium">
          {translate("welcome")}
        </Typography>
        <Typography
          color="textSecondary"
          sx={{
            paddingLeft: 0.5,
            textAlign: isSmUp ? "center" : "",
          }}
          variant="bodyMediumRegular"
        >
          {translate("loginAccountMessage")}
        </Typography>
      </Stack>
    </Stack>
  );
  const cardContent = (
    <form>
      <Stack sx={style.cardContent} data-testid={testId}>
        {cardHeader}
        {error && alert}
        <StandardTextField
          margin="normal"
          id="organizationID"
          size="small"
          variant="outlined"
          name="organizationID"
          value={watch("organizationID") ?? ""}
          fullWidth
          inputProps={register("organizationID", {
            required: {
              value: true,
              message: "requiredError",
            },
            validate: (value) =>
              value !== ":organizationId" || "invalidOrganizationId",
          })}
          data-testid="login-organization-id"
          label={translate("organizationID")}
          error={Boolean(errors.organizationID)}
          helperText={messageTrans(errors.organizationID?.message as string)}
        />
        <StandardTextField
          margin="normal"
          id="username"
          size="small"
          variant="outlined"
          type="text"
          name="username"
          value={watch("username") ?? ""}
          autoFocus
          fullWidth
          inputProps={register("username", {
            required: {
              value: true,
              message: "requiredError",
            },
          })}
          data-testid="login-username-field"
          label={translate("name")}
          error={Boolean(errors.username)}
          helperText={messageTrans(errors.username?.message as string)}
        />
        <PasswordTextField
          margin="normal"
          id="password"
          size="small"
          variant="outlined"
          type="password"
          name="password"
          fullWidth
          label={translate("password")}
          inputProps={register("password", {
            required: {
              value: true,
              message: "requiredError",
            },
          })}
          data-testid="login-password-field"
          error={Boolean(errors.password)}
          helperText={messageTrans(errors.password?.message as string)}
          showPasswordIcon={true}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            mb: 2,
            width: "100%",
          }}
        >
          <FormControlLabel
            sx={{
              m: 0,
              ".MuiFormControlLabel-label": {
                fontSize: 14,
                fontWeight: 500,
                ml: 0.5,
              },
            }}
            control={
              <Checkbox
                {...register("rememberMe")}
                checked={watch("rememberMe") || false}
                slotProps={{
                  input: { "data-testid": "login-remember-me" } as any,
                }}
                sx={{
                  p: 0.5,
                  color: colors.text.tertiary,
                  "&.Mui-checked": {
                    color: colors.brand.primary,
                  },
                }}
              />
            }
            label={translate("rememberMe")}
          />

          <Link
            component={RouterLink}
            to={`${
              watch("organizationID") ? `/${watch("organizationID")}` : ""
            }/forget-password`}
            underline="none"
            data-testid="login-forgot-password-link"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: colors.brand.primary,
            }}
          >
            {translate("forgetPassword")}
          </Link>
        </Box>
        {cardActions}
      </Stack>
    </form>
  );

  return isSmUp ? (
    <Grid container sx={style.container}>
      <Grid size={6}>
        <AuthLeftPanel />
      </Grid>
      <Grid size={6} sx={style.containerLogin}>
        {cardContent}
      </Grid>
    </Grid>
  ) : (
    <Grid container sx={style.containerMobileLog}>
      {cardContent}
    </Grid>
  );
}
